import * as THREE from 'three';
import { USDLoader } from 'three/examples/jsm/loaders/USDLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { ENDPOINTS } from '../config.js';
import { applyTransform } from './transform.js';
import { createModelSource } from './sceneSource.js';

/**
 * メッシュのスキャン（Scaniverse の Mesh モードで書き出した USDZ / GLB）を読み込む。
 * onProgress には 0〜1 の進捗（総バイト数が不明な場合は null）を渡す。
 */
export async function loadModel(scene, property, { onProgress } = {}) {
  const url = ENDPOINTS.model(property.model);
  const ext = property.model.split('.').pop().toLowerCase();
  const loader = ext === 'glb' || ext === 'gltf' ? new GLTFLoader() : ext === 'fbx' ? new FBXLoader() : new USDLoader();

  let root;
  try {
    const loaded = await loader.loadAsync(url, (event) => {
      if (typeof onProgress !== 'function') return;
      onProgress(event?.lengthComputable && event.total > 0 ? event.loaded / event.total : null);
    });
    root = loaded.scene ?? loaded;
  } catch (cause) {
    throw new Error(`スキャンデータの読み込みに失敗しました: ${url}`, { cause });
  }

  useBakedTextures(root);
  applyTransform(root, property.transform);
  scene.add(root);

  // メッシュには撮影開始地点の情報が無いので、最初の視点の足元を基準に当たり判定マップを作る
  const navOrigin = new THREE.Vector3().fromArray(property.viewpoints[0].position);
  return createModelSource(root, { navOrigin });
}

/**
 * スキャンのテクスチャには撮影時の明るさが焼き込まれている。
 * ライトで照らすと二重に陰影が付いて暗く濁るため、テクスチャをそのまま表示する。
 * 裏向きの面が抜けて部屋の外が見えないよう、両面を描く。
 */
function useBakedTextures(root) {
  root.traverse((node) => {
    if (!node.isMesh) return;
    const materials = Array.isArray(node.material) ? node.material : [node.material];
    const baked = materials.map((old) => {
      const next = new THREE.MeshBasicMaterial({
        map: old.map ?? null,
        color: old.map ? 0xffffff : (old.color ?? 0xcccccc),
        side: THREE.DoubleSide,
      });
      old.dispose();
      return next;
    });
    node.material = Array.isArray(node.material) ? baked : baked[0];
  });
}
